import {
	Center,
	createStyles,
	Group,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { UserAvatar } from '@repo/common/components';
import { AvatarSkeleton, Icon, IconButton, Text } from '@repo/foundations';
import { buttonHoverTransition } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef } from 'react';
import { Link } from 'react-router';
import { InlineEmojiPicker } from '../../../components/InlineEmojiPicker/InlineEmojiPicker';
import { formatShortTimeDifference } from '../../../utils/time';
import { AiQualityBadgeController } from '../../AiQualityBadge';
import type { QuestionStore } from '../store';
import { Reactions } from './Reactions';
import { ReplyMenu } from './ReplyMenu';
import { ViewportWrapper } from './ViewportWrapper';

const useStyles = createStyles(
	(
		theme,
		{ showToolbar, isFocused }: { showToolbar: boolean; isFocused: boolean }
	) => ({
		container: {
			borderRadius: theme.radius.md,
			boxShadow: theme.shadows.sm,
			border: isFocused
				? `2px solid ${theme.other.getColor('border/emphasis/default')}`
				: 'none',
			backgroundColor: isFocused
				? theme.other.getColor('surface/secondary/default')
				: theme.other.getColor('surface/primary/default'),
			padding: theme.spacing.md,
			alignItems: 'flex-start',
			gap: theme.spacing.sm,
			flexWrap: 'nowrap',

			'.show-on-hover': {
				opacity: showToolbar ? 1 : 0,
				...buttonHoverTransition('opacity'),
			},

			'&:hover': {
				[`.show-on-hover`]: {
					opacity: 1,
				},
			},
		},
		input: {
			// CSS for RichEditor
			flex: '1 !important', // The editor forces `flex-grow: 0` when in readOnly mode

			// Outline editor
			'& .ProseMirror > *:last-child': {
				marginBottom: 0,
			},
		},
	})
);

export const QuestionReplyBody = observer(
	({
		replyId,
		children,
		store,
		showToolbar,
		additionalToolbarButtons,
		isFocused,
	}: {
		replyId: string;
		children: React.ReactNode;
		store: QuestionStore;
		showToolbar?: boolean;
		additionalToolbarButtons?: React.ReactNode;
		isFocused?: boolean;
	}) => {
		const { classes } = useStyles({
			showToolbar: showToolbar ?? false,
			isFocused: isFocused ?? false,
		});

		const reply = store.replies.get(replyId);

		if (!reply) {
			return null;
		}

		const user = reply.reply.display_metadata?.owners?.[0];

		// Ref to store timeout ID for cleanup
		const timeoutRef = useRef<NodeJS.Timeout | null>(null);

		// Ref callback to scroll to focused reply when element is mounted/updated
		const containerRef = useCallback(
			(node: HTMLDivElement | null) => {
				// Clear any existing timeout
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
					timeoutRef.current = null;
				}

				if (node && isFocused) {
					// Small delay to ensure the component is fully rendered
					timeoutRef.current = setTimeout(() => {
						// Check if timeout is still valid before scrolling
						if (timeoutRef.current) {
							node.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
							timeoutRef.current = null;
						}
					}, 100);
				}
			},
			[isFocused]
		);

		return (
			<ViewportWrapper>
				<Group ref={containerRef} className={classes.container}>
					<Center py="3xs">
						{user ? (
							<UserAvatar user={user} size="xs" />
						) : (
							<AvatarSkeleton size="xs" />
						)}
					</Center>
					<Stack spacing="xs" style={{ flex: 1, minWidth: 0 }}>
						<Group spacing="xs" position="apart">
							<Group spacing="xs">
								<Text size="sm" weight="bold">
									{user?.display_name ?? 'Unknown'}
								</Text>
								<Text size="sm" color="text/secondary/default">
									{formatShortTimeDifference(
										reply.reply.created_at,
										false,
										false
									)}
								</Text>
								{reply.reply.jira_comment?.is_from_jira && (
									<UnstyledButton
										component={Link}
										to={reply.reply.jira_comment?.comment_url ?? ''}
									>
										<Text size="xs" color="text/secondary/default">
											via Jira
										</Text>
									</UnstyledButton>
								)}
								{reply.reply.embedded_prompt?.benchmark_raw?.score && (
									<AiQualityBadgeController
										score={reply.reply.embedded_prompt.benchmark_raw.score}
									/>
								)}
							</Group>
							<Group spacing="3xs">
								{additionalToolbarButtons}
								{store.permissions.canAccept &&
									(reply.reply.accepted_answer ? (
										<IconButton
											iconName="x"
											variant="tertiary"
											onClick={reply.toggleMarkAsAnswer}
											className="show-on-hover"
											tooltip="Reopen question"
										/>
									) : (
										<IconButton
											iconName="check"
											variant="tertiary"
											onClick={reply.toggleMarkAsAnswer}
											className="show-on-hover"
											tooltip="Mark as answer"
										/>
									))}
								{store.permissions.canReact && (
									<InlineEmojiPicker
										onClick={reply.toggleReaction}
										className="show-on-hover"
									/>
								)}
								{(reply.userCanEdit || reply.userCanDelete) && (
									<ReplyMenu
										onEdit={reply.userCanEdit ? reply.startEditing : undefined}
										onDelete={reply.delete}
										className="show-on-hover"
									/>
								)}
								{reply.reply.accepted_answer && (
									<Group spacing="2xs">
										<Icon name="check" color="icon/success/default" />
										<Text size="sm">Answer</Text>
									</Group>
								)}
							</Group>
						</Group>
						<Stack spacing="sm">
							<Stack className={classes.input} spacing="3xs">
								{children}
							</Stack>
							{!reply.editing && !!reply.reply.reactions?.length && (
								<Reactions
									reactions={reply.reply.reactions}
									onToggle={reply.toggleReaction}
									canReact={store.permissions.canReact}
								/>
							)}
						</Stack>
					</Stack>
				</Group>
			</ViewportWrapper>
		);
	}
);
